import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
    trips: ['M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'],
    users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    active: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3']
};

export default function TripReports({ user }) {
    const [trips, setTrips] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [zones, setZones] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ date: '', client: '', route: '', type: '', zone: '', driver: '', vendor: '' });

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, routeRes, zoneRes, allocationRes, driverRes, vendorRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/routes/${operatorId}`),
                axios.get(`${API}/master-data`),
                axios.get(`${API}/passenger-allocations/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setRoutes(routeRes.data);
            setZones(zoneRes.data.filter(z => z.category === 'route-zone'));
            setAllocations(allocationRes.data);
            setDrivers(driverRes.data);
            setVendors(vendorRes.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/trips/${operatorId}`);
            setTrips(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const filteredTrips = trips.filter(t => {
        if (filters.client && t.clientId?._id !== filters.client) return false;
        if (filters.route && t.routeId?._id !== filters.route) return false;
        if (filters.type && t.tripType !== filters.type) return false;
        if (filters.driver && t.driverId?._id !== filters.driver) return false;
        if (filters.vendor && t.vendorId?._id !== filters.vendor) return false;
        if (filters.zone) {
            const route = routes.find(r => r._id === t.routeId?._id);
            if (route?.zone?._id !== filters.zone) return false;
        }
        if (filters.date && t.tripDate) {
            if (t.tripDate.split('T')[0] !== filters.date) return false;
        }
        return true;
    });

    const exportToCSV = () => {
        const headers = ["Trip Route", "Type", "Client", "Frequency", "Schedule", "Passenger Count", "Driver", "Vendor", "Status"];
        const rows = filteredTrips.map(t => {
            const pCount = allocations.filter(a => a.tripId?._id === t._id).length;
            const schedule = t.tripFrequency === 'One Time' ? (t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—') : t.recurringDays?.join(' ');
            return [
                t.routeId?.name || '',
                t.tripType || '',
                t.clientId?.companyName || '',
                t.tripFrequency || '',
                `"${schedule}"`,
                pCount,
                t.driverId?.name || '—',
                t.vendorId?.vendorName || 'Self',
                t.status || ''
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Trip_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
    };

    const totalTrips = trips.length;
    const activeTrips = trips.filter(t => t.status === 'Active' || t.status === 'Scheduled').length;
    const totalPassengersAllocated = allocations.length;

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Trip Reports</h1>
                    <p className="md-subtitle">Analyze trip performance and passenger data.</p>
                </div>
                <button className="md-add-btn" onClick={exportToCSV} style={{ background: '#059669' }}>
                    <Icon d={icons.download} size={14} /> Export CSV
                </button>
            </div>

            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '25px' }}>
                <div className="md-panel" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ padding: '12px', background: '#eef2ff', color: '#16539C', borderRadius: '12px' }}>
                        <Icon d={icons.trips} size={24} />
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Total Trips</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>{totalTrips}</div>
                    </div>
                </div>
                <div className="md-panel" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ padding: '12px', background: '#ecfdf5', color: '#10b981', borderRadius: '12px' }}>
                        <Icon d={icons.active} size={24} />
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Active Trips</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>{activeTrips}</div>
                    </div>
                </div>
                <div className="md-panel" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ padding: '12px', background: '#fff7ed', color: '#f59e0b', borderRadius: '12px' }}>
                        <Icon d={icons.users} size={24} />
                    </div>
                    <div>
                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Passenger Allocation</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>{totalPassengersAllocated}</div>
                    </div>
                </div>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar" style={{ flexWrap: 'wrap', gap: '15px' }}>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Date</label>
                        <input type="date" className="md-input" value={filters.date} onChange={e => setFilters({ ...filters, date: e.target.value })} style={{ height: 38 }} />
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Client</label>
                        <select className="md-input" value={filters.client} onChange={e => setFilters({ ...filters, client: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Clients</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Route</label>
                        <select className="md-input" value={filters.route} onChange={e => setFilters({ ...filters, route: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Routes</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Zone</label>
                        <select className="md-input" value={filters.zone} onChange={e => setFilters({ ...filters, zone: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Zones</option>
                            {zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 120 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Type</label>
                        <select className="md-input" value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Types</option>
                            <option value="Login">Login</option>
                            <option value="Logout">Logout</option>
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Driver</label>
                        <select className="md-input" value={filters.driver} onChange={e => setFilters({ ...filters, driver: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Drivers</option>
                            {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 150 }}>
                        <label className="md-label" style={{ fontSize: 11 }}>Vendor</label>
                        <select className="md-input" value={filters.vendor} onChange={e => setFilters({ ...filters, vendor: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Vendors</option>
                            {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading reports...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Trip Details</th>
                                    <th className="md-th">Client Name</th>
                                    <th className="md-th">Schedule Info</th>
                                    <th className="md-th">Vehicle & Driver</th>
                                    <th className="md-th">Passengers</th>
                                    <th className="md-th">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTrips.map(t => {
                                    const tripAllocations = allocations.filter(a => a.tripId?._id === t._id);
                                    return (
                                        <tr key={t._id}>
                                            <td className="md-td">
                                                <div style={{ fontWeight: 700, color: '#063A5C' }}>{t.routeId?.name}</div>
                                                <div style={{ fontSize: 12, color: t.tripType === 'Login' ? '#16a34a' : '#ea580c' }}>
                                                    {t.tripType.toUpperCase()}
                                                </div>
                                            </td>
                                            <td className="md-td">{t.clientId?.companyName}</td>
                                            <td className="md-td">
                                                <div>{t.tripFrequency}</div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                                    {t.tripFrequency === 'One Time' ? (t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—') : t.recurringDays?.join(', ')}
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <div style={{ fontWeight: 600 }}>{t.driverId?.name || '—'}</div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>{t.vendorId?.vendorName || 'Self-Owned'}</div>
                                            </td>
                                            <td className="md-td">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontWeight: 700, fontSize: 15 }}>{tripAllocations.length} Passengers</div>
                                                    <div style={{ fontSize: 11, color: '#64748b', maxWidth: '300px' }}>
                                                        {tripAllocations.map(a => a.passengerId?.name).join(', ') || 'None'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="md-td">
                                                <span className="md-status-badge md-badge-active">{t.status}</span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
